import React, { Component } from 'react';
import SideMenu from '../admin/SideMenu';
import TopMenu from '../admin/TopBar';
import ExperienceChooserSetup from './ExperienceChooserSetup';
import { MIN_BUTTONS } from '../utils/experienceChooser';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import { appDatabasePrimaryFunctions } from '../../base';
import '../../styles/css/AdminMain.css';
import Swal from 'sweetalert2';
import { ErrorMessage, Toast } from '../utils/HelpfulFunction';
import { getScope, setScope, invalidateExperiences, DEFAULT_TEAM_ID } from '../utils/experienceScope';
import { currentSelectionFor, experienceFieldsFor } from '../utils/offerExperiences';

// Experiences = additional "team pages"/skins of the app (e.g. CV Firebirds,
// CV Lakers, Acrisure Arena). The DEFAULT experience is the app as it exists
// today — its config stays at the ROOT nodes (settings, homeButtonsBuilt,
// sideMenuOptions, teamPopulatedImage). Every OTHER experience keeps its own
// version of those nodes under experiences/{id}/ and the mobile app re-points
// its listeners there when a fan switches. Fans only see the "Change Experience"
// menu when 2 or more experiences exist.
//
// A new experience starts EMPTY, not as a clone of the default. A second team
// shares almost nothing with the first — different colours, different buttons,
// different menu — so a clone just means deleting someone else's content one
// row at a time before you can start. The app handles missing config: no
// settings falls back to a default theme, no bottomTabs means no tab bar, and
// empty button/menu nodes render as empty lists.

// Config screens linked from each experience row. The links carry
// ?experience=<id> so the target screen preselects that experience; the
// DEFAULT experience uses the bare route ('' = root config nodes).
const CONFIG_SCREENS = [
    { label: 'Settings', route: '/settings' },
    { label: 'Home Buttons', route: '/homebuttons' },
    { label: 'Side Menu', route: '/sidemenubuttons' },
    { label: 'Bottom Tabs', route: '/bottomtabs' },
    { label: 'Carousel', route: '/topcarousel' },
    { label: 'Radio', route: '/radio' }
];

class SetUpExperiences extends Component {
    constructor(props) {
        super(props);
        this.state = {
            experiences: {},
            loading: true,
            modal: false,
            editingId: null,
            name: '',
            // First run only: the name of the experience being ADDED, so the app
            // you already have and the one you came here to create are both
            // named in one step.
            newExperienceName: '',
            type: 'team',
            order: '',
            hockeytechClientCode: '',
            hockeytechTeamId: '',
            hockeytechLeague: '',
            hockeytechApiKey: '',
            ticketmasterApiKey: ''
        };
        this.handleChange = this.handleChange.bind(this);
        this.saveExperience = this.saveExperience.bind(this);
        this.clearForm = this.clearForm.bind(this);
        this.toggle = this.toggle.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        // The Choose your Experience editor below the list, so a delete can
        // take the dead experience out of what it is holding on screen.
        this.chooserSetup = React.createRef();
    }

    componentDidMount() {
        this.loadExperiences();
    }

    async loadExperiences() {
        try {
            const snapshot = await appDatabasePrimaryFunctions.ref('experiences').once('value');
            this.setState({ experiences: snapshot.val() || {}, loading: false });
        } catch (e) {
            console.log('Error loading experiences:', e);
            this.setState({ loading: false });
        }
    }

    handleChange(evt) {
        const target = evt.target;
        this.setState({ [target.name]: target.type === 'checkbox' ? target.checked : target.value });
    }

    clearForm() {
        this.setState({
            editingId: null,
            name: '',
            newExperienceName: '',
            type: 'team',
            order: '',
            hockeytechClientCode: '',
            hockeytechTeamId: '',
            hockeytechLeague: '',
            hockeytechApiKey: '',
            ticketmasterApiKey: ''
        });
    }

    // Opening and closing both clear the form, so a cancelled edit never leaks
    // its values into the next thing you open.
    toggle() {
        const open = !this.state.modal;
        this.clearForm();
        this.setState({ modal: open });
    }

    handleSubmit(event) {
        event.preventDefault();
        this.saveExperience();
    }

    // `alsoTaken` covers ids claimed earlier in the same save but not yet in
    // state — the first run writes two experiences before reloading.
    slugify(name, alsoTaken = []) {
        const base = (name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'experience';
        let slug = base;
        let counter = 2;
        // DEFAULT_TEAM_ID belongs to the club's FIRST experience, because a
        // roster uploaded before any experience existed is already keyed to it.
        // An experience that happened to be named "Default" must not take it,
        // or a later membership follows the wrong team.
        const reserved = (candidate) => candidate === DEFAULT_TEAM_ID
            && this.state.editingId !== DEFAULT_TEAM_ID;
        while ((this.state.experiences[slug] || alsoTaken.indexOf(slug) !== -1 || reserved(slug))
                && slug !== this.state.editingId) {
            slug = `${base}-${counter}`;
            counter++;
        }
        return slug;
    }

    // Publish or withdraw an experience.
    //
    // Withdrawing is not a delete: the config stays exactly where it is, so a
    // club can pull a themed skin after the night is over and put it back next
    // season. The default experience is never a draft — it IS the app.
    async setLive(id, live) {
        const exp = this.state.experiences[id] || {};
        // Hiding the default is how a club says "just this one for now" —
        // promote the experience you want, move the other out of sight, and
        // fans get one experience and no menu. That is a normal thing to want,
        // and it works: the resolver prefers the primary, so the drafted
        // default's config simply stops being read.
        //
        // It only fails if there is nothing to fall TO. With no primary the app
        // drops back to the root nodes — which are the default's own config —
        // so drafting it would achieve exactly nothing while looking like it
        // had. Refuse that one, and say which step is missing.
        if (exp.isDefault && !live) {
            const livePrimary = Object.values(this.state.experiences)
                .some(other => other && other.isPrimary && other.live !== false);
            if (!livePrimary) {
                ErrorMessage.fire({
                    title: 'Make another experience primary first',
                    text: `"${exp.name}" is what fans fall back to, so hiding it on its own would change nothing. Make the experience you want fans to see primary, then hide this one.`
                });
                return;
            }
        }
        if (!live && exp.isPrimary) {
            ErrorMessage.fire({
                title: 'This is the primary experience',
                text: `Fans are seeing "${exp.name}" right now. Make another experience primary first, then withdraw this one.`
            });
            return;
        }
        this.setState({ loading: true });
        try {
            await appDatabasePrimaryFunctions.ref(`experiences/${id}`).update({ live: !!live });
            invalidateExperiences();
            await this.loadExperiences();
            Toast.fire({ title: live ? 'Published' : 'Moved to draft' });
        } catch (e) {
            console.log(e);
            ErrorMessage.fire({ title: 'Could not save', text: 'Something went wrong updating that experience.' });
        }
        this.setState({ loading: false });
    }

    // Make one experience the one every fan gets.
    //
    // This is the alternative to asking fans to choose: a club builds its next
    // look as a draft, publishes it, then promotes it, and every app changes at
    // once. Reverting is the same action pointed back, because the overlay model
    // already keeps each non-default experience's config at experiences/{id} —
    // nothing moves.
    //
    // Exactly one experience carries the flag, so promoting clears every other
    // in the SAME multi-path update: two primaries would resolve by list order,
    // which is not a decision anyone made.
    async promote(id) {
        const exp = this.state.experiences[id] || {};
        if (exp.live === false) {
            ErrorMessage.fire({
                title: 'Publish it first',
                text: `"${exp.name}" is still a draft, so fans cannot see it. Publish it, then make it primary.`
            });
            return;
        }
        const current = Object.entries(this.state.experiences).find(([, e]) => e.isPrimary);
        const currentName = current ? current[1].name : this.defaultExperienceName();
        // Be honest about who actually moves. The primary is what a fan gets
        // when they have NOT chosen — so while the Change Experience menu is on,
        // a fan who picked one keeps it, and overriding that would be worse than
        // not moving them. Saying "every fan moves" is only true where fans
        // cannot choose, which is most clubs but not the one with the switcher.
        const switcherOn = Object.keys(this.state.experiences).length >= 2;
        const result = await Swal.fire({
            title: `Make "${exp.name}" what fans see?`,
            text: switcherOn
                ? `Fans who have not picked an experience themselves move from "${currentName}" to "${exp.name}" the next time their app loads. Anyone who chose one from the Change Experience menu keeps their choice. Nothing is deleted — you can point it back at any time.`
                : `Every fan moves from "${currentName}" to "${exp.name}" the next time their app loads. Nothing is deleted — you can point it back at any time.`,
            showCancelButton: true,
            confirmButtonText: 'Make primary'
        });
        if (!result.value) return;
        this.setState({ loading: true });
        try {
            const updates = {};
            Object.keys(this.state.experiences).forEach(otherId => {
                updates[`experiences/${otherId}/isPrimary`] = otherId === id;
            });
            await appDatabasePrimaryFunctions.ref().update(updates);
            invalidateExperiences();
            await this.loadExperiences();
            Toast.fire({ title: `Fans now see ${exp.name}` });
        } catch (e) {
            console.log(e);
            ErrorMessage.fire({ title: 'Could not save', text: 'Something went wrong promoting that experience.' });
        }
        this.setState({ loading: false });
    }

    // What fans see when nothing is explicitly promoted.
    defaultExperienceName() {
        const fallback = Object.values(this.state.experiences).find(e => e.isDefault);
        return fallback ? fallback.name : 'the default experience';
    }

    // Is the row being edited the club's own app? Its sports data is not
    // configured here — the poller reads the project's settings for it and
    // skips this record entirely.
    editingDefault() {
        const { editingId, experiences } = this.state;
        if (!editingId) return false;
        return !!(experiences[editingId] && experiences[editingId].isDefault);
    }

    async startEdit(id) {
        const exp = this.state.experiences[id] || {};
        // Open on everything already in hand; the private key is a second read
        // and the modal should not wait on the network to appear.
        this.setState({
            modal: true,
            editingId: id,
            newExperienceName: '',
            name: exp.name || '',
            type: exp.type || 'team',
            order: exp.order || '',
            hockeytechClientCode: (exp.sportsConfig && exp.sportsConfig.clientCode) || '',
            hockeytechTeamId: (exp.sportsConfig && exp.sportsConfig.teamId) || '',
            hockeytechLeague: (exp.sportsConfig && exp.sportsConfig.league) || '',
            hockeytechApiKey: '',
            ticketmasterApiKey: (exp.ticketmaster && exp.ticketmaster.apiKey) || ''
        });
        // The HockeyTech key lives in experiencesPrivate (the experiences node is fan-readable)
        try {
            const privateSnap = await appDatabasePrimaryFunctions.ref(`experiencesPrivate/${id}/hockeytechApiKey`).once('value');
            // Ignore a read that lands after the admin moved on.
            if (this.state.editingId === id) {
                this.setState({ hockeytechApiKey: privateSnap.val() || '' });
            }
        } catch (e) {
            console.log('Error loading private experience config:', e);
        }
    }

    async saveExperience() {
        const { experiences, editingId, name, newExperienceName, type, order } = this.state;
        if (!name || name.trim().length === 0) {
            ErrorMessage.fire({ title: 'Missing name', text: 'Give the experience a name — this is what fans see in the Change Experience menu.' });
            return;
        }
        if (name.trim().length > 40) {
            ErrorMessage.fire({ title: 'Name too long', text: 'Keep the experience name under 40 characters so it fits in the app menu.' });
            return;
        }
        const existingCount = Object.keys(experiences).length;
        const isFirst = existingCount === 0 && !editingId;
        // Past the highest order IN USE, not a count of the rows. Counting
        // meant deleting one and adding another handed the new one an order
        // somebody already had — two rows both claiming to be second, and the
        // list falling back to whatever order the database happened to return.
        const nextOrder = Object.values(experiences)
            .reduce((highest, exp) => Math.max(highest, Number(exp && exp.order) || 0), 0) + 1;
        // On the first run `name` names the app that already exists and
        // `newExperienceName` names the one being added. The second is optional
        // — leaving it blank just registers the current app and changes nothing
        // for fans.
        const secondName = isFirst ? (newExperienceName || '').trim() : '';
        if (secondName.length > 40) {
            ErrorMessage.fire({ title: 'Name too long', text: 'Keep the experience name under 40 characters so it fits in the app menu.' });
            return;
        }
        if (secondName && secondName.toLowerCase() === name.trim().toLowerCase()) {
            ErrorMessage.fire({ title: 'Same name twice', text: 'The two experiences need different names — fans pick between them by name.' });
            return;
        }
        if (isFirst) {
            const result = await Swal.fire({
                title: secondName ? 'Create these two experiences?' : 'Set up the default experience',
                text: secondName
                    ? `"${name.trim()}" will be your app exactly as it is set up today — nothing about it changes. "${secondName}" will be created empty, ready for you to build. Fans will then see a Change Experience menu with both.`
                    : `"${name.trim()}" will represent the app as it is configured today (all current settings, buttons and menus). After this you can add more experiences with their own look.`,
                showCancelButton: true,
                confirmButtonText: 'Create'
            });
            if (!result.value) return;
        }

        this.setState({ loading: true });
        try {
            // The first experience claims the id a roster may already use. A
            // club can upload a season ticket list long before it has any
            // reason to own a second app skin, and that list is keyed to
            // DEFAULT_TEAM_ID; letting the first experience take a slug off its
            // name instead would leave the roster and every entitlement on it
            // pointing at a team the admin no longer offers.
            const id = editingId || (isFirst ? DEFAULT_TEAM_ID : this.slugify(name));
            const existing = experiences[id] || {};
            const experience = {
                name: name.trim(),
                type: type || 'team',
                order: order ? parseInt(order, 10) : (existing.order || nextOrder),
                isDefault: editingId ? (existing.isDefault || false) : isFirst
            };
            const sportsConfig = {};
            if (this.state.hockeytechClientCode) sportsConfig.clientCode = this.state.hockeytechClientCode.trim();
            if (this.state.hockeytechTeamId) sportsConfig.teamId = this.state.hockeytechTeamId.trim();
            if (this.state.hockeytechLeague) sportsConfig.league = this.state.hockeytechLeague.trim().toLowerCase();
            if (Object.keys(sportsConfig).length > 0) {
                sportsConfig.provider = 'hockeytech';
            }
            const updates = {};
            // API key goes to experiencesPrivate — the experiences node is fan-readable
            updates[`experiencesPrivate/${id}/hockeytechApiKey`] = this.state.hockeytechApiKey ? this.state.hockeytechApiKey.trim() : null;
            updates[`experiences/${id}/name`] = experience.name;
            updates[`experiences/${id}/type`] = experience.type;
            updates[`experiences/${id}/order`] = experience.order;
            updates[`experiences/${id}/isDefault`] = experience.isDefault;
            updates[`experiences/${id}/sportsConfig`] = Object.keys(sportsConfig).length > 0 ? sportsConfig : null;
            updates[`experiences/${id}/ticketmaster`] = this.state.ticketmasterApiKey ? { apiKey: this.state.ticketmasterApiKey.trim() } : null;
            // A new experience starts as a DRAFT — invisible to fans — so a club
            // can build its next look without an empty skin appearing in every
            // fan's Change Experience menu the moment it is created. The FIRST
            // one is the app that already exists, so it is live from the start.
            // An edit leaves `live` alone: it is toggled from the list, not from
            // this form, and re-saving a form must not silently republish a
            // draft. Fans read `live !== false`, so an experience written before
            // this existed stays visible with no migration.
            if (!editingId) {
                updates[`experiences/${id}/live`] = isFirst;
            }

            // The added experience is created empty — no config nodes at all.
            // It picks up its look on the Settings / Home Buttons / Side Menu
            // screens, reached from its row below.
            if (secondName) {
                const secondId = this.slugify(secondName, [id]);
                updates[`experiences/${secondId}/name`] = secondName;
                updates[`experiences/${secondId}/type`] = 'team';
                updates[`experiences/${secondId}/order`] = experience.order + 1;
                updates[`experiences/${secondId}/isDefault`] = false;
                // Created empty, so it starts as a draft like any other.
                updates[`experiences/${secondId}/live`] = false;
            }

            await appDatabasePrimaryFunctions.ref().update(updates);

            invalidateExperiences();
            Toast.fire({ title: editingId ? 'Experience updated!' : (secondName ? 'Both experiences created!' : 'Experience created!') });
            this.clearForm();
            this.setState({ modal: false });
            await this.loadExperiences();
        } catch (e) {
            console.log('Error saving experience:', e);
            ErrorMessage.fire({ title: 'Uh oh!', text: 'Something went wrong saving the experience, check your connection and try again.' });
            this.setState({ loading: false });
        }
    }

    // Offers are NOT stored under experiences/{id} — they are a root node
    // scoped by field — so deleting an experience used to leave them pointing
    // at an id that no longer exists. Nothing then matched them: not the fan
    // app, and not this admin's own list, which filters by the same matcher.
    // They became content no one could reach or find.
    offerFallout(offers, id) {
        const experienceList = Object.entries(this.state.experiences)
            .map(([expId, exp]) => ({ id: expId, ...exp }));
        const stranded = [];   // ran ONLY here
        const shared = [];     // also runs elsewhere
        Object.entries(offers || {}).forEach(([offerId, offer]) => {
            const ids = currentSelectionFor(offer, experienceList);
            if (ids.indexOf(id) === -1) return;
            const remaining = ids.filter(x => x !== id);
            (remaining.length > 0 ? shared : stranded).push({ offerId, offer, remaining });
        });
        return { stranded, shared, experienceList };
    }

    async deleteExperience(id) {
        const exp = this.state.experiences[id] || {};
        if (exp.isDefault) {
            ErrorMessage.fire({ title: 'Cannot delete', text: 'The default experience is the app itself. You can rename it, but not delete it.' });
            return;
        }
        // Deleting the PRIMARY is deleting what every fan currently opens the
        // app into. The resolver falls back to the default cleanly, so nothing
        // breaks — but it happens silently, and the operator deleting a spent
        // themed skin is not necessarily the person who knows it is still the
        // one fans see. Make them move it first, so the change of look is a
        // deliberate act rather than a side effect of tidying up.
        if (exp.isPrimary) {
            ErrorMessage.fire({
                title: 'This is what fans see',
                text: `"${exp.name}" is the primary experience, so deleting it would change what every fan opens the app into. Make another experience primary first.`
            });
            return;
        }

        let fallout = { stranded: [], shared: [], experienceList: [] };
        let rosterCount = 0;
        try {
            const [offersSnap, rosterSnap] = await Promise.all([
                appDatabasePrimaryFunctions.ref('offers').once('value'),
                appDatabasePrimaryFunctions.ref(`sthRosterEmails/${id}`).once('value')
            ]);
            fallout = this.offerFallout(offersSnap.val() || {}, id);
            rosterCount = rosterSnap.numChildren();
        } catch (e) {
            console.log('Error checking what depends on this experience:', e);
        }
        // Whether it has a tile on the Choose your Experience screen, as far as
        // the editor below last read — only for the sentence in the dialog.
        // The delete itself reads the database again once the operator has
        // confirmed (see below): a dialog can stay open for minutes.
        const chooser = this.chooserSetup.current;
        const mentionChooserButton = !!(chooser && chooser.hasStoredButton(id));

        // Say what actually happens to each thing, with real counts, rather
        // than naming only the parts stored under the experience.
        const consequences = ['its settings, buttons, menus and carousel'];
        if (mentionChooserButton) consequences.push('its button on the Choose your Experience screen');
        if (fallout.shared.length > 0) consequences.push(`${fallout.shared.length} shared ${fallout.shared.length === 1 ? 'offer stays' : 'offers stay'} live in the other experiences`);
        if (fallout.stranded.length > 0) consequences.push(`${fallout.stranded.length} ${fallout.stranded.length === 1 ? 'offer that runs' : 'offers that run'} only here will be switched off and kept for you to reassign`);
        // The roster nodes are server-write-only in the rules, so this screen
        // cannot clear them however much it would like to. Say so rather than
        // promise it.
        // Down to one experience, the chooser editor gives way to a one-line
        // note — and takes any unsaved edits in it along, with no page unload
        // to ask first. Say so here, where the operator is already deciding.
        const remaining = Object.keys(this.state.experiences).filter(key => key !== id).length;
        const losesChooserEdits = remaining < MIN_BUTTONS && !!(chooser && chooser.hasUnsavedChanges());
        const keeps = rosterCount > 0
            ? ` Its season-ticket roster (${rosterCount} ${rosterCount === 1 ? 'email' : 'emails'}) is kept — only the server can remove that, and it matches nothing once the experience is gone.`
            : '';

        const result = await Swal.fire({
            title: `Delete "${exp.name}"?`,
            text: `This removes ${consequences.join('; ')}.${keeps} Fans currently on it fall back to the default experience.${losesChooserEdits ? ' Your unsaved changes to the Choose your Experience screen are discarded too: with one experience left, there is nothing to choose between.' : ''} This cannot be undone.`,
            type: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete'
        });
        if (!result.value) return;
        this.setState({ loading: true });

        // Its tile on the Choose your Experience screen goes with it — but only
        // when there IS one. A club whose database rules predate that screen
        // refuses every path under experienceChooser, and a multi-path update is
        // atomic, so naming the path unconditionally would fail every delete
        // there. Read on its own for the same reason: a refusal here must not
        // take the delete down with it. Read HERE, after the confirmation and
        // right before the write, so a tile another admin added while the
        // dialog was open still goes with the experience.
        //   true  — there is one, and this delete removes it
        //   false — there is none (the database said so)
        //   null  — could not tell; the delete leaves that path alone
        let chooserButton = null;
        try {
            const buttonSnap = await appDatabasePrimaryFunctions.ref(`experienceChooser/buttons/${id}`).once('value');
            chooserButton = buttonSnap.exists();
        } catch (e) {
            console.log('Error checking the Choose your Experience screen for this experience:', e);
        }

        try {
            const remainingExperiences = fallout.experienceList.filter(e => e.id !== id);
            const updates = {};
            updates[`experiences/${id}`] = null;
            updates[`experiencesPrivate/${id}`] = null;
            // In the same update, so the screen never offers fans a tile for an
            // experience that is already gone.
            if (chooserButton === true) updates[`experienceChooser/buttons/${id}`] = null;
            // sthRosterEmails and sthRosterAccounts are `.write: false` — only
            // the admin SDK touches them. Including them here would not just
            // fail on its own: a multi-path update is atomic, so one denied
            // path takes the whole delete down with it.

            fallout.shared.forEach(({ offerId, remaining }) => {
                const fields = experienceFieldsFor(remaining, remainingExperiences, remaining[0]);
                updates[`offers/${offerId}/experienceId`] = fields.experienceId;
                updates[`offers/${offerId}/experienceIds`] = fields.experienceIds;
            });
            // Switched off, not deleted: an offer can own a code pool and
            // per-fan assignments keyed by its id, and destroying those to tidy
            // up a deleted experience is not a trade an admin agreed to.
            fallout.stranded.forEach(({ offerId }) => {
                updates[`offers/${offerId}/active`] = false;
            });

            await appDatabasePrimaryFunctions.ref().update(updates);

            // entitlements are keyed entitlements/{uid}/{teamId}, so there is no
            // single path to clear and walking every fan to delete verified STH
            // grants is both slow and destructive. An orphaned one is inert —
            // nothing reads it once the experience is gone.

            // Otherwise the admin keeps editing an experience that no longer
            // exists, and every save recreates it as orphan config.
            if (getScope() === id) setScope('');
            invalidateExperiences();
            // Out of the editor's unsaved draft, or its next Save would write
            // the button straight back (and be refused by the rules, failing
            // the whole save). Out of its saved baseline too — but only when
            // the database is known not to hold it any more; after a failed
            // read the stored tile is probably still there, and the editor
            // keeps it as one Save must clear.
            if (this.chooserSetup.current) {
                this.chooserSetup.current.forgetExperience(id, { storedButtonGone: chooserButton !== null });
            }

            Toast.fire({ title: 'Experience deleted' });
            if (this.state.editingId === id) this.clearForm();
            await this.loadExperiences();
        } catch (e) {
            console.log('Error deleting experience:', e);
            ErrorMessage.fire({ title: 'Uh oh!', text: 'Something went wrong deleting the experience.' });
            this.setState({ loading: false });
        }
    }

    render() {
        const { experiences, editingId } = this.state;
        const list = Object.entries(experiences)
            .map(([id, exp]) => ({ id, ...exp }))
            // Ties break on the default first, then name. Orders can collide on
            // data written before they were allocated properly, and an
            // arbitrary order means the list reshuffles between loads for no
            // reason the operator can see.
            .sort((a, b) => (a.order || 0) - (b.order || 0)
                || (b.isDefault === true) - (a.isDefault === true)
                || String(a.name || '').localeCompare(String(b.name || '')));
        // What the FORM is doing (first run asks for two names) vs what the PAGE
        // is showing (an empty state until the first experience exists).
        const isFirst = list.length === 0 && !editingId;
        const noneYet = list.length === 0;
        return (
            <div className="admin-wrapper">
                <div className="loading-screen" style={{ display: this.state.loading ? 'block' : 'none' }}/>
                <SideMenu/>
                <TopMenu/>
                <div className="admin-main-panel">
                    <div className="admin-page-head">
                        <p className="admin-eyebrow">App layout</p>
                        <p className="admin-header-text">Experiences</p>
                        <p className="admin-page-sub">
                            Separate versions ("skins") of your app — a second team, or your arena. Each one has its own
                            settings, home buttons, side menu and bottom tabs, which you edit on those screens using the
                            Experience dropdown. Fans switch between them from the app side menu once you have more than one.
                        </p>
                    </div>

                    <div className="admin-toolbar">
                        <button className="btn btn-primary" onClick={this.toggle}>
                            {noneYet ? 'Set up experiences' : 'Add experience'}
                        </button>
                        {!noneYet &&
                            <span className="admin-toolbar-end">
                                <span className="admin-list-head-count">
                                    {list.length} {list.length === 1 ? 'experience' : 'experiences'}
                                    {list.length === 1 && ' \u00b7 fans see no switcher until there are two'}
                                </span>
                            </span>
                        }
                    </div>

                    {noneYet &&
                        <div className="admin-empty" style={{ margin: '18px 24px 0' }}>
                            <p className="admin-empty-title">No experiences yet</p>
                            <p className="admin-empty-body">
                                Your app runs on its own configuration today. Setting up experiences names that
                                configuration and lets you add another alongside it — nothing changes for fans until a
                                second experience exists.
                            </p>
                        </div>
                    }

                    <Modal isOpen={this.state.modal} toggle={this.toggle} style={{ width: '90%' }} id="myModal">
                        <div className="loading-screen" style={{ display: this.state.loading ? 'block' : 'none' }}/>
                        <ModalHeader toggle={this.toggle}>
                            {editingId
                                ? `Editing: ${experiences[editingId] ? experiences[editingId].name : editingId}`
                                : (isFirst ? 'Set up your experiences' : 'Add an experience')}
                        </ModalHeader>
                        <ModalBody>
                            <div className="container-out">
                                <div className="question-box question-form">
                                    <form className="pl-3 pr-3" onSubmit={this.handleSubmit}>
                                    {!editingId && !isFirst &&
                                        <p style={{ fontSize: 12, color: 'grey', fontWeight: 'normal' }}>
                                            A new experience starts empty. Give it its look on the Settings / Home Buttons /
                                            Side Menu Buttons / Bottom Tabs / Top Carousel screens, reached from its row below.
                                        </p>
                                    }
                                    {isFirst &&
                                        <p style={{ fontSize: 12, color: 'grey', fontWeight: 'normal' }}>
                                            Your app as it stands today becomes the first experience — nothing about it changes.
                                            Name it, and name the one you want to add alongside it.
                                        </p>
                                    }
                                    <div className="form-group">
                                        <label htmlFor="name">{isFirst ? 'What your app is called today' : 'Name (shown to fans)'}</label>
                                        {isFirst &&
                                            <p style={{ fontSize: 12, fontWeight: 'normal', color: 'grey' }}>
                                                Your current settings, buttons and menus stay exactly as they are under this name.
                                            </p>
                                        }
                                        <input type="text" id="name" name="name" className="form-control" value={this.state.name} onChange={this.handleChange} placeholder={isFirst ? (process.env.REACT_APP_CLEAN_CLIENT_NAME || 'Your club name') : 'Stadium Experience'}/>
                                    </div>
                                    {isFirst &&
                                        <div className="form-group">
                                            <label htmlFor="newExperienceName">Name of the experience you're adding (optional)</label>
                                            <p style={{ fontSize: 12, fontWeight: 'normal', color: 'grey' }}>
                                                Created empty for you to build. Leave blank to just register your current app for now —
                                                fans see no change until a second experience exists.
                                            </p>
                                            <input type="text" id="newExperienceName" name="newExperienceName" className="form-control" value={this.state.newExperienceName} onChange={this.handleChange} placeholder="Second team or brand name"/>
                                        </div>
                                    }
                                    {/* The default experience uses the app's existing config —
                                        type/order/sports/ticketing fields only apply to additional experiences */}
                                    {!isFirst && <>
                                    <div className="form-group">
                                        <label htmlFor="type">Type</label>
                                        <select id="type" name="type" className="form-control" style={{ maxWidth: 300 }} value={this.state.type} onChange={this.handleChange}>
                                            <option value="team">Team</option>
                                            <option value="venue">Venue / Arena</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="order">Order</label>
                                        <p style={{ fontSize: 12, fontWeight: 'normal', color: 'grey' }}>Position in the Change Experience list (1 = first)</p>
                                        <input type="number" id="order" name="order" className="form-control" style={{ maxWidth: 150 }} value={this.state.order} onChange={this.handleChange} placeholder="1"/>
                                    </div>
                                    {/* The DEFAULT experience's sports data comes from the
                                        project's own configuration, not from here — the poller
                                        skips isDefault outright (functions/index.js). Showing
                                        these fields on that row let an operator fill them in,
                                        save successfully, and wait forever for a schedule that
                                        was never going to arrive. Say where it comes from
                                        instead of offering a form that does nothing. */}
                                    {this.state.type === 'team' && this.editingDefault() &&
                                        <div style={{ borderTop: '1px solid #e5e5e5', marginTop: 20, paddingTop: 15 }}>
                                            <p style={{ fontWeight: 'bold', marginBottom: 0 }}>Sports data</p>
                                            <p style={{ fontSize: 12, color: 'grey' }}>
                                                This is your main app, so its schedule, roster and standings come from
                                                the settings we hold for you rather than from this screen. Ask SQWAD if
                                                any of it needs changing. Additional experiences set their own here.
                                            </p>
                                        </div>
                                    }
                                    {this.state.type === 'team' && !this.editingDefault() &&
                                        <div style={{ borderTop: '1px solid #e5e5e5', marginTop: 20, paddingTop: 15 }}>
                                            <p style={{ fontWeight: 'bold', marginBottom: 0 }}>Sports data (optional)</p>
                                            <p style={{ fontSize: 12, color: 'grey' }}>
                                                Only needed if this experience is a different team with its own schedule/roster/standings.
                                                Ask SQWAD for the HockeyTech values. Leave blank for now if you don't have them.
                                            </p>
                                            <div className="form-group">
                                                <label htmlFor="hockeytechClientCode">HockeyTech client code</label>
                                                <input type="text" id="hockeytechClientCode" name="hockeytechClientCode" className="form-control" style={{ maxWidth: 300 }} value={this.state.hockeytechClientCode} onChange={this.handleChange} placeholder="ahl"/>
                                            </div>
                                            <div className="form-group">
                                                <label htmlFor="hockeytechTeamId">HockeyTech team ID</label>
                                                <input type="text" id="hockeytechTeamId" name="hockeytechTeamId" className="form-control" style={{ maxWidth: 300 }} value={this.state.hockeytechTeamId} onChange={this.handleChange} placeholder="445"/>
                                            </div>
                                            <div className="form-group">
                                                <label htmlFor="hockeytechLeague">League</label>
                                                <input type="text" id="hockeytechLeague" name="hockeytechLeague" className="form-control" style={{ maxWidth: 300 }} value={this.state.hockeytechLeague} onChange={this.handleChange} placeholder="ahl"/>
                                            </div>
                                            <div className="form-group">
                                                <label htmlFor="hockeytechApiKey">HockeyTech API key (optional)</label>
                                                <p style={{ fontSize: 12, fontWeight: 'normal', color: 'grey' }}>Only if this team's league needs a different HockeyTech key than the app's default. Stored privately — never readable by the app.</p>
                                                <input type="text" id="hockeytechApiKey" name="hockeytechApiKey" className="form-control" style={{ maxWidth: 300 }} value={this.state.hockeytechApiKey} onChange={this.handleChange} placeholder=""/>
                                            </div>
                                        </div>
                                    }
                                    <div className="form-group">
                                        <label htmlFor="ticketmasterApiKey">Ticketmaster API key (optional)</label>
                                        <p style={{ fontSize: 12, fontWeight: 'normal', color: 'grey' }}>Only if this experience uses a different Ticketmaster account than the default</p>
                                        <input type="text" id="ticketmasterApiKey" name="ticketmasterApiKey" className="form-control" value={this.state.ticketmasterApiKey} onChange={this.handleChange} placeholder=""/>
                                    </div>
                                    </>}
                                        <div className="admin-modal-actions">
                                            <button type="button" className="btn btn-secondary" onClick={this.toggle}>Cancel</button>
                                            <button className="btn btn-primary submit-button" id="submitButton">
                                                {editingId
                                                    ? 'Save Changes'
                                                    : (isFirst
                                                        ? (this.state.newExperienceName.trim() ? 'Create Both Experiences' : 'Create Default Experience')
                                                        : 'Create Experience')}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </ModalBody>
                    </Modal>

                    {list.length > 0 && <>
                        <div className="admin-list-head">
                            <h3>Your experiences</h3>
                        </div>
                        <div className="admin-table-wrap">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Order</th>
                                        <th>Name</th>
                                        <th>Type</th>
                                        <th>Sports Feed</th>
                                        <th/>
                                    </tr>
                                </thead>
                                <tbody>
                                    {list.map(exp => (
                                        <tr key={exp.id}>
                                            <td>{exp.order || '-'}</td>
                                            <td>
                                                {exp.name}{exp.isDefault ? <span style={{ color: 'grey', fontSize: 12 }}> (default — uses the app's main configuration)</span> : ''}
                                                {/* State, not an action — a draft is not a
                                                    problem, it is a thing being worked on.
                                                    "Hidden in the app" rather than "Draft" alone,
                                                    because the record itself stays readable: the
                                                    fan app filters drafts out, it does not keep
                                                    them secret, and an operator should not put
                                                    anything confidential in one on the strength
                                                    of a badge. */}
                                                {exp.live === false &&
                                                    <span className="admin-tag" style={{ marginLeft: 8 }}
                                                          title="Fans do not see this experience. It is hidden from the app, not kept secret — don't put anything confidential in it.">
                                                        Draft &mdash; hidden in the app
                                                    </span>
                                                }
                                                {exp.isPrimary &&
                                                    <span className="admin-tag is-strong" style={{ marginLeft: 8 }}>What fans see</span>
                                                }
                                                <div style={{ fontSize: 12, marginTop: 4 }}>
                                                    {CONFIG_SCREENS.map((screen, i) => (
                                                        <span key={screen.route}>
                                                            {i > 0 && ' · '}
                                                            <a href={exp.isDefault ? screen.route : `${screen.route}?experience=${exp.id}`}>{screen.label}</a>
                                                        </span>
                                                    ))}
                                                </div>
                                            </td>
                                            <td>{exp.type === 'venue' ? 'Venue' : 'Team'}</td>
                                            <td>{exp.sportsConfig && exp.sportsConfig.teamId ? `${exp.sportsConfig.clientCode || ''} #${exp.sportsConfig.teamId}` : '—'}</td>
                                            <td>
                                                <button type="button" className="admin-icon-btn" aria-label="Edit experience" title="Edit experience" onClick={() => this.startEdit(exp.id)}><span className="fa fa-pencil" aria-hidden="true"/></button>
                                                {/* The default gets this too. Hiding it is how a club
                                                    says "just this one for now" — promote the other,
                                                    hide this. setLive refuses the one case that would
                                                    not work (nothing else promoted) and says which step
                                                    is missing, which is a better teacher than a button
                                                    that is not there at all. */}
                                                <button type="button" className="btn btn-sm btn-secondary" style={{ marginLeft: 6 }}
                                                        onClick={() => this.setLive(exp.id, exp.live === false)}>
                                                    {exp.live === false ? 'Publish' : 'Move to draft'}
                                                </button>
                                                {!exp.isPrimary && exp.live !== false &&
                                                    <button type="button" className="btn btn-sm btn-secondary" style={{ marginLeft: 6 }}
                                                            onClick={() => this.promote(exp.id)}>
                                                        Make primary
                                                    </button>
                                                }
                                                {!exp.isDefault &&
                                                    <button type="button" className="admin-icon-btn is-danger" aria-label="Delete experience" title="Delete experience" onClick={() => this.deleteExperience(exp.id)}><span className="fa fa-trash-o" aria-hidden="true"/></button>
                                                }
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>}

                    {/* Mounted once there are two experiences to choose between,
                        and kept mounted across reloads, so a save or delete up
                        here never throws away work in progress down there. With
                        one, the screen can never show (the app needs two tiles),
                        so a whole editor would only invite setting up something
                        inert — one line says what it takes instead. */}
                    {list.length >= MIN_BUTTONS &&
                        <ExperienceChooserSetup ref={this.chooserSetup} experiences={experiences}/>
                    }
                    {list.length > 0 && list.length < MIN_BUTTONS &&
                        <section className="admin-chooser">
                            <div className="admin-list-head">
                                <h3>Choose your Experience screen</h3>
                            </div>
                            <p className="admin-page-sub admin-chooser-sub">
                                Add a second experience to use this screen — it lets fans pick between experiences after they sign in.
                            </p>
                        </section>
                    }
                </div>
            </div>
        );
    }
}

export default SetUpExperiences;
